
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getMasterDatabaseRecords } from "@/modules/adminPortal/api/masterDatabase.api";

import type {
  MasterDatabaseRecord,
  MasterDatabaseStats,
} from '@/modules/adminPortal/api/masterDatabase.api';
import { startLoading, stopLoading } from "@/store/loaderSlice";
import {
  fetchMasterDatabaseRecords,
  createMasterDatabaseRecordThunk,
  importMasterDatabaseRecords,
  updateMasterDatabaseRecordThunk,
} from '@/modules/adminPortal/redux/masterDatabaseThunk';
import {
  mapMasterDatabaseRecord,
  type MasterDatabaseDisplayRecord,
} from './masterDatabase/masterDatabaseDisplay.utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  Search,
  Download,
  MoreHorizontal,
  Eye,
  Pencil,
  Phone,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  RefreshCw,
  FileText,
  PhoneCall,
  Upload,
} from 'lucide-react';

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  const text = String(value).trim();
  return text === '—' || text === '-' ? '' : text;
};

const toDateInputValue = (value: unknown): string => {
  const text = formatCellValue(value);
  if (!text) return '';

  const dayFirst = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dayFirst) {
    return `${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const isoDate = text.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoDate) return isoDate[1];

  return '';
};


const MASTER_DB_COLUMNS: Array<{
  key: keyof MasterDatabaseRecord;
  label: string;
}> = [
    { key: 'serialNo', label: 'S No.' },
    { key: 'candidateId', label: 'Candidate ID' },
    { key: 'candidateName', label: 'Candidate Name' },
    { key: 'ageing', label: 'Ageing' },
    { key: 'sharedCandidate', label: 'Shared Candidate' },
    { key: 'candidateBlacklisted', label: 'Candidate Blacklisted' },
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'hrName', label: 'HR Name' },
    { key: 'contactNo', label: 'Contact No.' },
    { key: 'location', label: 'Location' },
    { key: 'entity', label: 'Entity' },
    { key: 'position', label: 'Position' },
    { key: 'hodName', label: 'HOD Name' },
    { key: 'reportingManager', label: 'Reporting Manager' },
    { key: 'source', label: 'Source' },
    { key: 'subSource', label: 'Sub Source' },
    { key: 'screeningDate', label: 'Screening Date' },
    { key: 'callStatus', label: 'Call Status' },
    { key: 'currentCompany', label: 'Current Company' },
    { key: 'totalExperience', label: 'Total Experience' },
    { key: 'realStateExperience', label: 'Real State Experience' },
    { key: 'currentInhand', label: 'Current Inhand' },
    { key: 'expectedInhand', label: 'Expected Inhand' },
    { key: 'pfNoPf', label: 'PF/No PF' },
    { key: 'noticePeriod', label: 'Notice period' },
    { key: 'interviewLineup', label: 'Interview Lineup (Yes/No)' },
    { key: 'interviewLinedupDate', label: 'Interview Linedup date' },
    { key: 'hrInterviewDate', label: 'HR Interview Date' },
    { key: 'hrInterviewName', label: 'HR Interview Name' },
    { key: 'hrInterviewStatus', label: 'HR Interview Status' },
    { key: 'business1Date', label: 'Business-1 Date' },
    {
      key: 'business1InterviewerName',
      label: 'Business-1 Interviewer Name',
    },
  ];

const inputClass =
  'h-8 min-w-[130px] whitespace-nowrap border border-slate-300 bg-white text-sm focus-visible:ring-1 focus-visible:ring-blue-300';

const isBooleanField = (key: string) =>
  /blacklisted|sharedcandidate|interviewlineup/i.test(key);

const isDateField = (key: string) => /date$/i.test(key);

const isStatusField = (key: string) =>
  /status/i.test(key) || key === 'callStatus';

const isYesNoField = (key: string) =>
  /blacklisted|sharedcandidate|interviewlineup/i.test(key);

export default function MasterDatabasePage() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPortal = location.pathname.startsWith('/admin');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');

  const [selectedRecord, setSelectedRecord] =
    useState<MasterDatabaseDisplayRecord | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importError, setImportError] = useState('');

  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [editValues, setEditValues] =
    useState<Partial<MasterDatabaseRecord>>({});
  const [savingRecord, setSavingRecord] = useState(false);
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const [editError, setEditError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportError, setExportError] = useState("");

  const {
    records: apiRecords,
    loading,
    stats: backendStats,
    creating: creatingRecord,
  } = useSelector(
    (state: {
      masterDatabase: {
        records: MasterDatabaseRecord[];
        loading: boolean;
        stats: MasterDatabaseStats | null;
        creating: boolean;
      };
    }) => state.masterDatabase,
  );

  const loadRecords = async () => {
    await dispatch(
      fetchMasterDatabaseRecords({
        page: 1,
        limit: 100,
        search: searchQuery.trim(),
      }),
    ).unwrap();
  };

  const handleSync = async () => {
    dispatch(startLoading());

    try {
      await loadRecords();
    } catch (error) {
      console.error("Master Database sync failed:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  useEffect(() => {
    void dispatch(
      fetchMasterDatabaseRecords({
        page: 1,
        limit: 100,
        search: searchQuery.trim(),
      }),
    );
  }, [dispatch, searchQuery]);

  const masterDbRecords = useMemo(
    () => apiRecords.map(mapMasterDatabaseRecord),
    [apiRecords],
  );

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          masterDbRecords
            .map((record) => record.finalStatus)
            .filter(Boolean),
        ),
      ).sort(),
    [masterDbRecords],
  );

  const entityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          masterDbRecords
            .map((record) => record.entity)
            .filter(Boolean),
        ),
      ).sort(),
    [masterDbRecords],
  );

  const filteredRecords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return masterDbRecords.filter((record) => {
      const rawRecord = apiRecords.find((item) => item.id === record.id);

      const matchesSearch =
        !query ||
        record.candidateName.toLowerCase().includes(query) ||
        record.candidateCode.toLowerCase().includes(query) ||
        record.email.toLowerCase().includes(query) ||
        record.contact.includes(query) ||
        Object.values(rawRecord ?? {}).some((value) =>
          String(value ?? '').toLowerCase().includes(query),
        );

      const matchesStatus =
        filterStatus === 'all' ||
        record.finalStatus === filterStatus ||
        (filterStatus === 'blacklisted' && record.isBlacklisted);

      const matchesEntity =
        filterEntity === 'all' || record.entity === filterEntity;

      return matchesSearch && matchesStatus && matchesEntity;
    });
  }, [
    masterDbRecords,
    apiRecords,
    searchQuery,
    filterStatus,
    filterEntity,
  ]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'bg-blue-100 text-blue-700';
      case 'in progress':
        return 'bg-yellow-100 text-yellow-700';
      case 'offered':
      case 'passed':
        return 'bg-green-100 text-green-700';
      case 'hired':
        return 'bg-green-600 text-white';
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'on hold':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCallStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'connected':
        return 'bg-green-100 text-green-700';
      case 'no answer':
      case 'busy':
      case 'voicemail':
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'failed':
      case 'not connected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget;
    const file = input.files?.[0];

    if (!file) return;

    setImportMessage('');
    setImportError('');

    const allowedExtensions = ['.csv', '.xls', '.xlsx'];
    const extension = file.name
      .slice(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setImportError(
        'Unsupported file type. Please select a CSV, XLS, or XLSX file.',
      );
      input.value = '';
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setImportError(
        'File size exceeds 20 MB. Please split the file and import it in smaller parts.',
      );
      input.value = '';
      return;
    }

    setImporting(true);

    try {
      const result = await dispatch(
        importMasterDatabaseRecords(file),
      ).unwrap();

      setImportMessage(
        result.message ||
        `${result.data?.importedCount ?? 0} records imported successfully.`,
      );

      await loadRecords();
    } catch (error) {
      setImportError(
        typeof error === 'string'
          ? error
          : 'Failed to import records. Please try again.',
      );
    } finally {
      setImporting(false);
      input.value = '';
    }
  };

  
const handleExportCsv = async () => {
  setExporting(true);
  setExportMessage("");
  setExportError("");

  try {
    const pageSize = 100;
    const search = searchQuery.trim();

    // Fetch the first page to determine the total number of pages.
    const firstResponse = await getMasterDatabaseRecords({
      page: 1,
      limit: pageSize,
      search,
    });

    const allRecords: MasterDatabaseRecord[] = [
      ...(firstResponse.data ?? []),
    ];

    const totalPages = Math.max(
      1,
      firstResponse.pagination?.totalPages ?? 1,
    );

    // Fetch all remaining pages instead of exporting only the first 100.
    for (let page = 2; page <= totalPages; page += 1) {
      const response = await getMasterDatabaseRecords({
        page,
        limit: pageSize,
        search,
      });

      allRecords.push(...(response.data ?? []));
    }

    // Apply the currently selected status and entity filters.
    const exportRecords = allRecords.filter((record) => {
      const displayRecord = mapMasterDatabaseRecord(record);

      const matchesStatus =
        filterStatus === "all" ||
        displayRecord.finalStatus === filterStatus ||
        (filterStatus === "blacklisted" &&
          displayRecord.isBlacklisted);

      const matchesEntity =
        filterEntity === "all" ||
        displayRecord.entity === filterEntity;

      return matchesStatus && matchesEntity;
    });

    // Match the Master Database columns and include the Logs column.
    const exportColumns = [
      ...MASTER_DB_COLUMNS,
      { key: "logs" as const, label: "Logs" },
    ];

    const escapeCsvCell = (value: unknown): string => {
      const text = formatCellValue(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const header = exportColumns
      .map((column) => escapeCsvCell(column.label))
      .join(",");

    const rows = exportRecords.map((record) =>
      exportColumns
        .map((column) =>
          escapeCsvCell(record[column.key]),
        )
        .join(","),
    );

    // BOM helps Excel open UTF-8 CSV files correctly.
    const csvContent = "\uFEFF" + [header, ...rows].join("\r\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = `master-database-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);

    setExportMessage(
      `Successfully exported ${exportRecords.length} record${
        exportRecords.length === 1 ? "" : "s"
      } to CSV.`,
    );
  } catch (error: unknown) {
    const message =
      typeof error === "object" &&
      error !== null &&
      "response" in error
        ? (
            error as {
              response?: { data?: { message?: string } };
            }
          ).response?.data?.message
        : undefined;

    setExportError(
      message ||
        (error instanceof Error
          ? error.message
          : "Failed to export Master Database records. Please try again."),
    );
  } finally {
    setExporting(false);
  }
};


  const startEditing = (record: MasterDatabaseRecord) => {
    setIsAddingRecord(false);
    setEditingRecordId(record.id);
    setEditValues({ ...record });
    setEditError('');
  };

  const cancelEditing = () => {
    setEditingRecordId(null);
    setEditValues({});
    setEditError('');
  };

  const startAddingRecord = () => {
    setIsAddingRecord(true);
    setEditingRecordId(null);

    setEditValues(
      Object.fromEntries(
        MASTER_DB_COLUMNS.map(({ key }) => [key, '']),
      ) as Partial<MasterDatabaseRecord>,
    );

    setEditError('');
    setImportMessage('');
    setImportError('');
  };

  const cancelAddingRecord = () => {
    setIsAddingRecord(false);
    setEditValues({});
    setEditError('');
  };

  const updateField = (
    key: keyof MasterDatabaseRecord,
    value: unknown,
  ) => {
    setEditValues((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveNewRecord = async () => {
    setEditError('');
    setSavingRecord(true);

    try {
      const payload = Object.fromEntries(
        MASTER_DB_COLUMNS.map(({ key }) => [
          key,
          formatCellValue(editValues[key] ?? ''),
        ]),
      ) as Partial<
        Omit<MasterDatabaseRecord, 'id' | 'logs' | 'createdAt' | 'updatedAt'>
      >;

      const result = await dispatch(
        createMasterDatabaseRecordThunk(payload),
      ).unwrap();

      setIsAddingRecord(false);
      setEditValues({});
      setImportMessage(
        result.message || 'Master database record created successfully.',
      );

      await loadRecords();
    } catch (error) {
      setEditError(
        typeof error === 'string'
          ? error
          : 'Failed to create Master Database record. Please try again.',
      );
    } finally {
      setSavingRecord(false);
    }
  };

  const saveRecord = async (record: MasterDatabaseRecord) => {
    setSavingRecord(true);
    setEditError('');

    try {
      const payload = {
        ...editValues,
      } as Partial<MasterDatabaseRecord>;

      delete payload.id;
      delete payload.logs;
      delete payload.createdAt;
      delete payload.updatedAt;

      await dispatch(
        updateMasterDatabaseRecordThunk({
          id: record.id,
          payload: payload as Partial<
            Omit<
              MasterDatabaseRecord,
              'id' | 'logs' | 'createdAt' | 'updatedAt'
            >
          >,
        }),
      ).unwrap();

      setEditingRecordId(null);
      setEditValues({});

      await loadRecords();
    } catch (error) {
      setEditError(
        typeof error === 'string'
          ? error
          : 'Failed to save record. Please try again.',
      );
    } finally {
      setSavingRecord(false);
    }
  };

  const renderEditableField = (
    key: keyof MasterDatabaseRecord,
    fallbackValue?: unknown,
  ) => {
    const rawValue = editValues[key] ?? fallbackValue;
    const keyString = String(key);
    const currentValue = formatCellValue(rawValue);

    if (isBooleanField(keyString)) {
      const normalizedValue = currentValue
        ? /^(yes|true|1)$/i.test(currentValue)
          ? 'Yes'
          : 'No'
        : '__empty__';

      return (
        <Select
          value={normalizedValue}
          onValueChange={(value) =>
            updateField(key, value === '__empty__' ? '' : value)
          }
        >
          <SelectTrigger className={inputClass}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__empty__">Select...</SelectItem>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    return (
      <Input
        type={isDateField(keyString) ? 'date' : 'text'}
        value={
          isDateField(keyString)
            ? toDateInputValue(rawValue)
            : currentValue
        }
        onChange={(event) => updateField(key, event.target.value)}
        className={inputClass}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Page heading and toolbar */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            Master Recruitment Database
          </h1>
          <p className="text-muted-foreground">
            Complete candidate tracking across all hiring stages
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            className="hidden"
            onChange={handleImportFile}
          />

          <Button
            variant="outline"
            className="w-full sm:w-auto"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
          >

            <Download className="mr-2 h-4 w-4" />
            {importing ? 'Importing...' : 'Import Excel / CSV'}
          </Button>

          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => void handleSync()}
            disabled={loading || importing}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Sync Data
          </Button>

          
         <Button
           variant="outline"
           disabled={exporting || loading || importing}
           onClick={() => void handleExportCsv()}
          >
         <Upload className="mr-2 h-4 w-4" />
         {exporting ? "Exporting..." : "Export CSV"}
         </Button>

        </div>
      </div>

      {(importMessage || importError) && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${importError
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-800'
            }`}
        >
          {importError || importMessage}
        </div>
      )}

      
{(exportMessage || exportError) && (
  <div
    role={exportError ? "alert" : "status"}
    className={`rounded-lg border px-4 py-3 text-sm ${
      exportError
        ? "border-red-200 bg-red-50 text-red-700"
        : "border-green-200 bg-green-50 text-green-800"
    }`}
  >
    {exportError || exportMessage}
  </div>
)}


      {/* Summary cards: dynamic backend response values only */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold">
              {backendStats?.total ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Total Records</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-blue-600">
              {backendStats?.new ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">New</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-yellow-600">
              {backendStats?.inProgress ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-green-600">
              {backendStats?.offered ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Offered</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-green-700">
              {backendStats?.hired ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Hired</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-red-600">
              {backendStats?.rejected ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-2xl font-bold text-purple-600">
              {backendStats?.voiceCallCompleted ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">Voice Calls</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, email, phone..."
                className="h-11 pl-10"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>

            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <Select
                value={filterStatus}
                onValueChange={setFilterStatus}
              >
                <SelectTrigger className="w-full md:w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                  <SelectItem value="blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filterEntity}
                onValueChange={setFilterEntity}
              >
                <SelectTrigger className="w-full md:w-[160px]">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityOptions.map((entity) => (
                    <SelectItem key={entity} value={entity}>
                      {entity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            Showing {filteredRecords.length} of {masterDbRecords.length} records
          </p>

          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={startAddingRecord}
              disabled={isAddingRecord || editingRecordId !== null || loading}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Add Record
            </Button>
          </div>
        </CardContent>
      </Card>

      {editError && (
        <p className="mb-2 text-sm text-red-600">{editError}</p>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="w-full overflow-x-auto rounded-lg">
            <Table className="min-w-[2600px] [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
              <TableHeader>
                <TableRow>
                  {MASTER_DB_COLUMNS.map((column) => (
                    <TableHead
                      key={String(column.key)}
                      className="whitespace-nowrap"
                    >
                      {column.label}
                    </TableHead>
                  ))}

                  <TableHead className="whitespace-nowrap">
                    Logs
                  </TableHead>

                  <TableHead className="min-w-[110px] whitespace-nowrap text-right">
                    Edit
                  </TableHead>

                  <TableHead className="whitespace-nowrap text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={MASTER_DB_COLUMNS.length + 3}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Loading records...
                    </TableCell>
                  </TableRow>
                ) : filteredRecords.length === 0 && !isAddingRecord ? (
                  <TableRow>
                    <TableCell
                      colSpan={MASTER_DB_COLUMNS.length + 3}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {isAddingRecord && (
                      <TableRow className="bg-blue-50/40 outline outline-1 outline-inset outline-blue-300 [&>td]:border-y [&>td]:border-blue-200">
                        {MASTER_DB_COLUMNS.map((column) => (
                          <TableCell
                            key={String(column.key)}
                            className="whitespace-nowrap text-sm"
                          >
                            {renderEditableField(column.key, '')}
                          </TableCell>
                        ))}

                        <TableCell className="min-w-[180px] whitespace-nowrap text-xs text-muted-foreground" />

                        <TableCell className="whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              disabled={savingRecord || creatingRecord}
                              onClick={() => void saveNewRecord()}
                            >
                              {savingRecord || creatingRecord
                                ? 'Saving...'
                                : 'Save'}
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              disabled={savingRecord || creatingRecord}
                              onClick={cancelAddingRecord}
                            >
                              Cancel
                            </Button>
                          </div>
                        </TableCell>

                        <TableCell className="text-right" />
                      </TableRow>
                    )}

                    {filteredRecords.map((record) => {
                      const rawRecord = apiRecords.find(
                        (item) => item.id === record.id,
                      );

                      if (!rawRecord) return null;

                      const isEditing = editingRecordId === record.id;

                      return (
                        <TableRow
                          key={record.id}
                          className={[
                            record.isBlacklisted ? 'bg-red-50' : '',
                            isEditing
                              ? 'bg-blue-50/40 outline outline-1 outline-inset outline-blue-300 [&>td]:border-y [&>td]:border-blue-200'
                              : '',
                          ].join(' ')}
                        >
                          {MASTER_DB_COLUMNS.map((column) => {
                            const key = column.key;
                            const rawValue = rawRecord[key];

                            const currentValue = isEditing
                              ? (editValues[key] ?? rawValue)
                              : rawValue;

                            const displayValue = formatCellValue(currentValue);
                            const normalizedKey = String(key).toLowerCase();
                            const statusField = isStatusField(normalizedKey);
                            const yesNoField = isYesNoField(normalizedKey);
                            const booleanField = isBooleanField(normalizedKey);

                            return (
                              <TableCell
                                key={String(key)}
                                className="whitespace-nowrap text-sm"
                              >
                                {isEditing ? (
                                  booleanField ? (
                                    <Select
                                      value={
                                        displayValue
                                          ? /^(yes|true|1)$/i.test(displayValue)
                                            ? 'Yes'
                                            : 'No'
                                          : '__empty__'
                                      }
                                      onValueChange={(value) =>
                                        updateField(
                                          key,
                                          value === '__empty__' ? '' : value,
                                        )
                                      }
                                    >
                                      <SelectTrigger className={inputClass}>
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__empty__">
                                          Select...
                                        </SelectItem>
                                        <SelectItem value="Yes">Yes</SelectItem>
                                        <SelectItem value="No">No</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    renderEditableField(key, rawValue)
                                  )
                                ) : statusField ? (
                                  displayValue ? (
                                    <Badge
                                      variant="secondary"
                                      className={
                                        normalizedKey === 'callstatus'
                                          ? getCallStatusColor(displayValue)
                                          : getStatusColor(displayValue)
                                      }
                                    >
                                      {displayValue}
                                    </Badge>
                                  ) : null
                                ) : yesNoField ? (
                                  displayValue ? (
                                    <Badge
                                      variant="secondary"
                                      className={
                                        /^(yes|true|1|blacklisted)$/i.test(
                                          displayValue,
                                        )
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-gray-100 text-gray-600'
                                      }
                                    >
                                      {displayValue}
                                    </Badge>
                                  ) : null
                                ) : (
                                  displayValue
                                )}
                              </TableCell>
                            );
                          })}

                          <TableCell className="min-w-[180px] whitespace-nowrap text-xs text-muted-foreground">
                            {formatCellValue(rawRecord.logs)}
                          </TableCell>

                          <TableCell className="whitespace-nowrap text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  disabled={savingRecord}
                                  onClick={() => void saveRecord(rawRecord)}
                                >
                                  {savingRecord ? 'Saving...' : 'Save'}
                                </Button>

                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={savingRecord}
                                  onClick={cancelEditing}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Edit row"
                                onClick={() => startEditing(rawRecord)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Keep navigation inside the portal from which Master Database was opened.
                                    const detailsBasePath = isAdminPortal
                                      ? '/admin/master-db/candidates'
                                      : '/hr/candidates';

                                    const applicationId = (
                                      rawRecord as MasterDatabaseRecord & {
                                        applicationId?: string | null;
                                      }
                                    ).applicationId?.trim();

                                    if (applicationId) {
                                      navigate(
                                        `${detailsBasePath}/${encodeURIComponent(applicationId)}`,
                                      );
                                      return;
                                    }

                                    navigate(
                                      `${detailsBasePath}/master-db/${encodeURIComponent(rawRecord.id)}`,
                                    );
                                  }}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() => startEditing(rawRecord)}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit Record
                                </DropdownMenuItem>

                                <DropdownMenuItem>
                                  <Phone className="mr-2 h-4 w-4" />
                                  Trigger Voice Call
                                </DropdownMenuItem>

                                <DropdownMenuItem>
                                  <FileText className="mr-2 h-4 w-4" />
                                  View Resume
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Candidate details dialog */}
      <Dialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
      >
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Candidate Details - {selectedRecord?.candidateCode}
            </DialogTitle>
            <DialogDescription>
              Complete information from master database
            </DialogDescription>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">
                      Personal Information
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">
                        {selectedRecord.candidateName}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Contact</span>
                      <span>{selectedRecord.contact}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email</span>
                      <span className="text-xs">{selectedRecord.email}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span>{selectedRecord.location}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">
                      Position Details
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entity</span>
                      <span className="font-medium">
                        {selectedRecord.entity}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Position</span>
                      <span>{selectedRecord.position}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">HOD</span>
                      <span>{selectedRecord.hod}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Reporting To
                      </span>
                      <span>{selectedRecord.reportingManager}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">
                      Experience &amp; Salary
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Experience</span>
                      <span className="font-medium">
                        {selectedRecord.experience}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current CTC</span>
                      <span>Rs.{selectedRecord.currentSalary} LPA</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Expected CTC
                      </span>
                      <span>Rs.{selectedRecord.expectedSalary} LPA</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Notice Period
                      </span>
                      <span>{selectedRecord.noticePeriod} days</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold">
                    Interview Progress
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full ${selectedRecord.hrInterviewStatus === 'Passed'
                            ? 'bg-green-100'
                            : selectedRecord.hrInterviewStatus === 'Failed'
                              ? 'bg-red-100'
                              : 'bg-gray-100'
                          }`}
                      >
                        {selectedRecord.hrInterviewStatus === 'Passed' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : selectedRecord.hrInterviewStatus === 'Failed' ? (
                          <XCircle className="h-4 w-4 text-red-600" />
                        ) : (
                          <Clock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>

                      <div>
                        <p className="text-sm font-medium">HR Interview</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedRecord.hrInterviewStatus}
                        </p>
                      </div>
                    </div>

                    <div className="hidden h-0.5 w-16 bg-gray-200 xl:block" />

                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full ${selectedRecord.businessRound1Status === 'Passed'
                            ? 'bg-green-100'
                            : selectedRecord.businessRound1Status === 'Failed'
                              ? 'bg-red-100'
                              : 'bg-gray-100'
                          }`}
                      >
                        <Clock className="h-4 w-4 text-gray-400" />
                      </div>

                      <div>
                        <p className="text-sm font-medium">Business Round 1</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedRecord.businessRound1Status || 'Pending'}
                        </p>
                      </div>
                    </div>

                    <div className="h-0.5 w-16 bg-gray-200" />

                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                        <Clock className="h-4 w-4 text-gray-400" />
                      </div>

                      <div>
                        <p className="text-sm font-medium">Business Round 2</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedRecord.businessRound2Status || 'Pending'}
                        </p>
                      </div>
                    </div>

                    <div className="h-0.5 w-16 bg-gray-200" />

                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                        <CheckCircle2 className="h-4 w-4 text-gray-400" />
                      </div>

                      <div>
                        <p className="text-sm font-medium">Offer</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedRecord.offerDate || 'Pending'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {selectedRecord.voiceCallCompleted && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                      <PhoneCall className="h-4 w-4" />
                      Voice Call Details
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Call Status
                        </span>
                        <Badge
                          variant="secondary"
                          className={getCallStatusColor(
                            selectedRecord.callStatus,
                          )}
                        >
                          {selectedRecord.callStatus}
                        </Badge>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Screening Date
                        </span>
                        <span>{selectedRecord.screeningDate}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Real Estate Exp
                        </span>
                        <Badge
                          variant={
                            selectedRecord.realEstateExperience
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {selectedRecord.realEstateExperience ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedRecord.isBlacklisted && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Ban className="h-6 w-6 text-red-600" />
                      <div>
                        <p className="font-semibold text-red-700">
                          Blacklisted Candidate
                        </p>
                        <p className="text-sm text-red-600">
                          Reason: {selectedRecord.blacklistedReason}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowDetailDialog(false)}
            >
              Close
            </Button>

            <Button
              onClick={() => {
                const rawRecord = apiRecords.find(
                  (item) => item.id === selectedRecord?.id,
                );

                if (rawRecord) {
                  setShowDetailDialog(false);
                  startEditing(rawRecord);
                }
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
